import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";

export default function MessageComposer({
    consultationObject = "",
    message = {},
    errors = {},
    loading = false,
    maxCharacters = 5000,
    onChange,
    onSubmit,
    onBack,

}) {

    const { t } = useTranslation();
    const MAX_FILE_SIZE_MB = 20;

    
    //----------------------------------------------------
    // Configuration
    //----------------------------------------------------
    const MAX_FILE_SIZE = 20 * 1024 * 1024; //10 Mo
    const MAX_FILES = 4;
    const ALLOWED_EXTENSIONS = [
        "pdf",
        "jpg",
        "jpeg",
        "png",
        
        "doc",
        "docx",

        "xls",
        "xlsx",

        "ppt",
        "pptx",

        "mp4"

    ];

    //----------------------------------------------------

    const inputFile = useRef(null);
    const [fileError, setFileError] = useState("");
    //----------------------------------------------------

    const openFileChooser = () => {
        inputFile.current.click();
    };

    //----------------------------------------------------
    // Validation fichier
    //----------------------------------------------------

    const validateFile = (file) => {
        //------------------------------------
        // Taille
        //------------------------------------
        if (file.size > MAX_FILE_SIZE) {
            return t("messageComposer.fileTooLarge", {
                fileName: file.name,
                maxSize: `${MAX_FILE_SIZE_MB} Mo`
            });

        }

        //------------------------------------
        // Extension
        //------------------------------------

        const extension =
            file.name
                .split(".")
                .pop()
                .toLowerCase();
        if (
            !ALLOWED_EXTENSIONS.includes(
                extension
            )
        ) {
            return t("messageComposer.invalidExtension", {
                extension
            });
        }
        //------------------------------------
        return null;

    };


    //----------------------------------------------------
    // Ajout des fichiers
    //----------------------------------------------------

    const handleFiles = (e) => {
        setFileError("");
        const selectedFiles =
            Array.from(e.target.files);
        //------------------------------------
        // Fichiers déjà présents
        //------------------------------------

        const currentFiles = [
            message.attachedFile1,
            message.attachedFile2,
            message.attachedFile3,
            message.attachedFile4

        ].filter(Boolean);


        //------------------------------------
        // Maximum 4 fichiers
        //------------------------------------

        if (
            currentFiles.length +
            selectedFiles.length >
            MAX_FILES
        ) {

            setFileError(
             t("messageComposer.maxFilesReached", {maxFiles: MAX_FILES })
            );
            inputFile.current.value = "";
            return;

        }


        //------------------------------------
        // Validation
        //------------------------------------

        for (const file of selectedFiles) {
            const error = validateFile(file);
            if (error) {
                setFileError(error);
                inputFile.current.value = "";
                return;
            }

        }


        //------------------------------------
        // Ajout
        //------------------------------------

        const allFiles = [
            ...currentFiles,
            ...selectedFiles
        ];


        allFiles.forEach((file, index) => {
            onChange({
                target: {
                    name:
                        `attachedFile${index + 1}`,
                    type: "file",
                    value: file,
                    files: [file]
                }
            });
        });
        inputFile.current.value = "";
    };


    //----------------------------------------------------
    // Suppression
    //----------------------------------------------------

    const removeFile = (indexToRemove) => {

        const files = [
            message.attachedFile1,
            message.attachedFile2,
            message.attachedFile3,
            message.attachedFile4

        ].filter(Boolean);


        files.splice(indexToRemove, 1);

        for (let i = 0; i < MAX_FILES; i++) {

            const file =
                files[i] || null;
            onChange({
                target: {
                    name:
                        `attachedFile${i + 1}`,
                    type: "file",
                    value: file,
                    files:
                        file
                            ? [file]
                            : []
                }
            });
        }
    };


    //----------------------------------------------------

    const files = [
        message.attachedFile1,
        message.attachedFile2,
        message.attachedFile3,
        message.attachedFile4
    ].filter(Boolean);


    //----------------------------------------------------

    const disableSubmit =
        loading ||
        fileError.length > 0;
    //----------------------------------------------------

    return (
        <form
            onSubmit={onSubmit}
            encType="multipart/form-data"
            className="card shadow border-0 mt-4"

        >

            {/* HEADER */}
            <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                    <i className="bi bi-chat-left-text me-2"></i>
                    {t("button.reply")}
                </h5>

            </div>


            {/* BODY */}

            <div className="card-body">


                {/* Objet */}

                <div className="mb-3">

                    <label className="form-label">
                        {t("messageComposer.subject")}
                    </label>

                    <input

                        type="text"

                        readOnly

                        value={consultationObject}

                        className="form-control bg-light fw-bold"

                    />

                </div>


                {/* Message */}

                <div className="mb-3">
                    <label className="form-label">
                        {t("messageComposer.message")}
                    </label>

                    <textarea

                        rows={6}

                        name="messageBody"

                        value={message.messageBody || ""}

                        onChange={onChange}

                        className={`form-control ${errors.messageBody
                                ? "is-invalid"
                                : ""
                            }`}

                    />

                </div>


                <div className="text-end mb-4">

                    <small className="text-muted">

                        {

                            (message.messageBody || "")
                                .length

                        }

                        {" / "}

                        {maxCharacters}

                    </small>

                </div>


                {/* Pièces jointes */}

                <div className="border rounded bg-light p-3">

                    <div className="d-flex justify-content-between align-items-center mb-3">

                        <strong>

                            <i className="bi bi-paperclip me-2"></i>

                            {t("common.attachments")}

                            {

                                files.length > 0 &&

                                ` (${files.length}/4)`

                            }

                        </strong>


                        {

                            files.length < MAX_FILES &&

                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={openFileChooser}

                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                {t("button.add")}
                            </button>

                        }

                    </div>


                    <input

                        hidden

                        multiple

                        ref={inputFile}

                        type="file"

                        onChange={handleFiles}

                        accept={

                            ".pdf," +
                            ".jpg," +
                            ".jpeg," +
                            ".png," +
                            ".doc," +
                            ".docx," +
                            ".xls," +
                            ".xlsx," +
                            ".ppt," +
                            ".pptx," +
                            ".mp4"

                        }

                    />


                    {

                        fileError &&

                        <div className="alert alert-danger">

                            <i className="bi bi-exclamation-triangle-fill me-2"></i>

                            {fileError}

                        </div>

                    }


                    {

                        files.length === 0 &&

                        <div className="text-center text-muted py-4">

                            <i className="bi bi-paperclip fs-2 d-block mb-2"></i>

                            {t("common.noAttachment")}

                        </div>

                    }


                    {

                        files.map((file, index) => (

                            <div

                                key={index}

                                className="d-flex justify-content-between align-items-center border rounded bg-white p-2 mb-2"

                            >

                                <div>

                                    <i className="bi bi-file-earmark me-2 text-success"></i>

                                    {file.name}

                                </div>

                                <button

                                    type="button"

                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() =>
                                        removeFile(index)
                                    }
                                >
                                    <i className="bi bi-x-lg"></i>

                                </button>
                            </div>
                        ))
                    }

                    <div className="mt-3 small text-muted">
                        {t("common.allowedFormats")} 
                        PDF, JPG, JPEG, PNG,
                        DOC, DOCX, XLS,
                        XLSX, PPT, PPTX {t("common.and")} MP4.
                        <br />
                           {t("common.maxSize")}

                        <strong> 20 Mo</strong>.
                    </div>
                </div>
            </div>

 {/* FOOTER */}
<div className="card-footer bg-white">

    <div className="d-flex justify-content-between">

        <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
        >
            <i className="bi bi-arrow-left me-2"></i>
            {t("button.back")}
        </button>

        <button
            type="submit"
            disabled={disableSubmit}
            className="btn btn-success"
        >
            {
                loading
                    ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            {t("button.sending")}
                        </>
                    )
                    : (
                        <>
                            <i className="bi bi-send-fill me-2"></i>
                            {t("button.send")}
                        </>
                    )
            }
        </button>

    </div>

</div>

        </form>

    );


    
}